import { Grid } from '@interstate/components/Grid';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import * as dealerDiscountsSelectors from '@makemydeal/dr-dash-store/selectors/dealerDiscountsSelectors';
import type { DealHistory, StateTree } from '@makemydeal/dr-dash-types';
import { useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDealSummary } from '../../common/DealSummaryProvider';
import * as constants from '../../constants';
import DealSummaryItem from '../common/DealSummaryItem';
import LineItemWithChildren from '../common/LineItemWithChildren';
import { defaultGridProps, detailedViewGridProps, detailedViewValueStyles } from '../utils/styles';
import type { DetailedBreakdown } from '@makemydeal/dr-platform-types';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';

export type DiscountsOptions = {
    deal?: DealHistory;
    hasDelta?: boolean;
    showNegativeDiscount?: boolean;
};

export const DealerDiscounts = (discountsOptions: DiscountsOptions) => {
    const { deal, hasDelta = false } = discountsOptions;

    const selectDealerDiscounts = useMemo(
        () => (state: StateTree) => dealerDiscountsSelectors.getSummaryDealerDiscounts(state, deal),
        [deal]
    );

    const dealerDiscounts =
        useTrackedSelector(selectDealerDiscounts, { component: 'DealerDiscounts', field: 'dealerDiscounts' }) ||
        ({} as DetailedBreakdown);
    const dealerDiscountsTotal = dealerDiscounts.value;
    const dealerDiscountsItems = dealerDiscounts.items || [];

    const hasItems = Boolean(dealerDiscountsItems.length);

    const { showDetailedView } = useDealSummary();

    const discountLabel = constants.DEALER_DISCOUNTS;

    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const gridProps = showDetailedView && isLargeSize ? detailedViewGridProps : defaultGridProps;

    return dealerDiscountsTotal ? (
        <>
            <DealSummaryItem
                label={discountLabel}
                value={formatDollarsAndCents(dealerDiscountsTotal)}
                hasDelta={hasDelta}
                valueStyles={showDetailedView ? detailedViewValueStyles : {}}
            >
                {hasItems ? (
                    <Grid container sx={gridProps}>
                        {dealerDiscountsItems.map((item, index) => (
                            <LineItemWithChildren key={index} label={item.name} value={formatDollarsAndCents(item.value)} />
                        ))}
                    </Grid>
                ) : null}
            </DealSummaryItem>
        </>
    ) : null;
};

export default DealerDiscounts;
